import React, { useContext } from 'react';
import type { FC } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { useSessionData } from '@confluence/session-data';
import { SPAViewContext } from '@confluence/spa-view-context';
import {
	ExperienceAbort,
	ExperienceFailure,
	ExperienceSuccess,
	TRIAL_COMPONENTS_LOZENGE_VIEW_EXPERIENCE,
} from '@confluence/experience-tracker';
import { useIsPremiumOnboardingFeatureDiscoveryEnabled } from '@confluence/premium-onboarding/entry-points/useIsPremiumOnboardingFeatureDiscoveryEnabled';
import { markErrorAsHandled } from '@confluence/graphql';
import { isUnauthorizedError } from '@confluence/error-boundary';

import { useHasBillingInformation } from '../useHasBillingInformation';
import { getTrialDaysLeftFromQuery } from '../graphql/getTrialDaysLeftFromQuery';
import { TrialDetailsQuery } from '../graphql/TrialDetailsQuery.graphql';
import type { TrialDetailsQuery as TrialDetailsQueryType } from '../graphql/__types__/TrialDetailsQuery';

import PremiumLozengeImage from './premiumLozenge.svg';

type LozengeProps = {
	edition: ConfluenceEdition;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeContainer = styled.div({
	display: 'inline-block',
	height: '18px',
	verticalAlign: 'text-top',
	paddingInline: token('space.100', '8px'),
});

export const Lozenge: FC<LozengeProps> = ({ edition }) => {
	const { isLicensed, isLoggedIn } = useSessionData();
	const {
		isSiteAdmin,
		loading: spaViewContextLoading,
		error: spaViewContextError,
	} = useContext(SPAViewContext);

	const isAdmin = isSiteAdmin && !spaViewContextLoading && !spaViewContextError;
	const { isPremiumOnboardingFeatureDiscoveryEnabled } =
		useIsPremiumOnboardingFeatureDiscoveryEnabled({
			shouldCheckIsInTrial: false,
		});
	const shouldSkipQueryForNonAdmin = !isPremiumOnboardingFeatureDiscoveryEnabled && !isAdmin;
	const skipQuery = !isLicensed || !isLoggedIn || shouldSkipQueryForNonAdmin;
	const {
		data: trialDetailsData,
		loading: trialDetailsLoading,
		error: trialDetailsError,
	} = useQuery<TrialDetailsQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		TrialDetailsQuery,
		{
			skip: skipQuery,
		},
	);

	const trialDaysLeft = getTrialDaysLeftFromQuery(trialDetailsData);
	const isInTrial = trialDaysLeft && trialDaysLeft > 0;

	// Setup generic component for different values of edition
	const image = edition === ConfluenceEdition.PREMIUM && PremiumLozengeImage;
	const alt = edition === ConfluenceEdition.PREMIUM ? 'Premium' : '';
	const datatestId = `${edition.toLowerCase()}-trial-components-lozenge`;

	const { hasBillingInformation } = useHasBillingInformation(!isInTrial);

	if (trialDetailsLoading) {
		return null;
	}

	if (trialDetailsError) {
		// `TrialDetailsQuery` can throw Permission exception if initiater doesn't have sufficient permission
		// Permission error in this case is expected and FE should handle it gracefully - "markErrorAsHandled" and abort the experience instead of reporting as failures.
		if (isUnauthorizedError(trialDetailsError)) {
			markErrorAsHandled(trialDetailsError);

			return (
				<ExperienceAbort
					name={TRIAL_COMPONENTS_LOZENGE_VIEW_EXPERIENCE}
					reason="Aborted due to insufficient permission"
				/>
			);
		}

		return (
			<ExperienceFailure
				error={trialDetailsError}
				name={TRIAL_COMPONENTS_LOZENGE_VIEW_EXPERIENCE}
				attributes={{ isLicensed, isLoggedIn, isAdmin, isInTrial }}
			/>
		);
	}

	if (skipQuery || !isInTrial || hasBillingInformation) {
		return (
			<ExperienceSuccess
				name={TRIAL_COMPONENTS_LOZENGE_VIEW_EXPERIENCE}
				attributes={{ isLicensed, isLoggedIn, isAdmin, isInTrial }}
			/>
		);
	}

	return (
		<>
			<LozengeContainer>
				<img src={image} height={18} width={64} alt={alt} data-testid={datatestId} />
			</LozengeContainer>
			<ExperienceSuccess
				name={TRIAL_COMPONENTS_LOZENGE_VIEW_EXPERIENCE}
				attributes={{ isLicensed, isLoggedIn, isAdmin, isInTrial }}
			/>
		</>
	);
};
